import map from 'lodash/map';
import filter from 'lodash/filter';
import { removeEdgesFromBlockPort } from 'utils/flow/flow';
import forEach from 'lodash/forEach';
import { AlertTriggerType } from 'xm-constants/triggerTypes';
import {
  getTriggerDisplayName,
  isIncidentPropertyChangeTrigger,
} from 'xm-constants/incidentTriggers';

export const IDEAL_NAME_KEY = 'xmUIIdealName';

// Intentionally not translated as these will be saved as the
// Integration name and Flow Block label values in the API:
const INTEGRATION_TYPE_NAMES = {
  ANNOTATION: 'Alert Comments',
  DELIVERYSTATUS: 'Device Delivery Updates',
  ESCALATION: 'Escalations',
  STATUS: 'Alert Status Updates',
  TARGETEDRECIPIENTFAILURE: 'Targeted Recipient Failures',
  RESPONSE: 'Response',
  RESPONSEOPTION: 'Response Option',
};

/**
 * Get the display name of step config dialogs' drag & drop variables.
 * For example, with Create Alert with Status dropdown, this will return
 * "Create Alert - Status Alert Status Update" as the variable name.
 *
 * This function is specific to blocks with subtriggers; this is not a generic
 * function to get drag & drop variable's display names. For the generic version,
 * see utils/placeholderInputs.js => getVariableParts.
 *
 * @param block - The parent block, e.g. Create Alert, Initiate Incident, etc.
 * @param triggerPort - The sub trigger, e.g. RESPONSE, CRITICAL, etc.
 * @returns {string} - The display name. Returns '' if operation fails.
 */
export function getIdealIntegrationName(block = {}, triggerPort = {}) {
  const { data, triggerType } = triggerPort;
  // For Response Options default to the text. It should _always_ have text available,
  // but just in case falls back to "Response Option":
  const nameSuffix =
    (triggerType === AlertTriggerType.RESPONSEOPTION && data?.text) ||
    INTEGRATION_TYPE_NAMES[triggerType];

  if (isIncidentPropertyChangeTrigger(triggerType)) {
    return getTriggerDisplayName(triggerType);
  }

  return [block.label, nameSuffix].filter(n => n).join(' - ');
}

export function syncSubTriggerBlocksByMutatingCanvas({
  block,
  customSteps = [],
  getStepDataFunction,
  planId,
  selectedCanvas,
  triggerPorts,
}) {
  const { id } = block;
  const blockTriggerIds = new Set(map(triggerPorts, 'triggerBlockId'));
  const existingTriggerBlocksById = {};
  let resultEdges = selectedCanvas.edges;
  let resultInputs = selectedCanvas.inputs;
  let blocksChanged = false;

  /**
   * Remove no longer relevant blocks (and any associated edges):
   */
  const resultBlocks = filter(selectedCanvas.blocks, blk => {
    if (blk.groupingBlockId !== id) {
      return true; // Unrelated block, skip it, DO NOT DISCARD!
    }
    const currBlockId = blk.id;
    if (blockTriggerIds.has(currBlockId)) {
      // Already had a block for this trigger, keep it and make sure we don't add it later:
      existingTriggerBlocksById[currBlockId] = blk;
      return true;
    }

    // Block is grouped under the alertBlock, but not one of the latest triggerPorts so remove:
    blocksChanged = true;
    resultEdges = removeEdgesFromBlockPort(resultEdges, id, currBlockId);
    resultInputs = filter(
      resultInputs,
      input => input.inputPort !== currBlockId,
    );
    return false;
  });

  /**
   * Add blocks for new triggers who don't have a block yet:
   */
  forEach(triggerPorts, triggerPort => {
    const existingBlock = existingTriggerBlocksById[triggerPort.triggerBlockId];

    const updatedStepData = getStepDataFunction(
      triggerPort,
      block,
      planId,
      existingBlock?.step,
      customSteps,
    );
    const label = getIdealIntegrationName(block, triggerPort);

    if (existingBlock) {
      // TODO: Set blocksChanged here based on if underlying, saveable data changed.
      // It is not strictly necessary right now, but modifying the block objects in place feels a bit sketchy.
      existingBlock.step = updatedStepData;
      existingBlock.label = label;
    } else {
      // No block yet, create one with metadata about hte integration to create:
      blocksChanged = true;
      resultBlocks.push({
        ...block,
        id: triggerPort.triggerBlockId,
        groupingBlockId: block.id,
        label,
        ports: [],
        presentation: {},
        step: updatedStepData,
      });
    }
  });

  // Mutate selectedCanvas if necessary (as this method's name implies):
  if (blocksChanged) {
    /* eslint-disable no-param-reassign */
    selectedCanvas.blocks = resultBlocks;
    selectedCanvas.edges = resultEdges;
    selectedCanvas.inputs = resultInputs;
    /* eslint-enable no-param-reassign */
  }
}
